import { Children, type ReactNode } from 'react';

import { Intent } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { sanitizeProps } from '../../core/slots/toolbar/CompactToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import { CategoryAxis } from '../slots/CategoryAxis.js';
import { CategoricalBarChartDownloadCSVSlot } from '../slots/DownloadCSV.js';
import { Legend } from '../slots/Legend.js';
import {
  CategoricalBarChartThresholdIndicatorProps,
  ThresholdIndicator,
} from '../slots/ThresholdIndicator.js';
import { CategoricalBarChartToolbarSlot } from '../slots/Toolbar.js';
import { Tooltip } from '../slots/Tooltip.js';
import { ValueAxis } from '../slots/ValueAxis.js';
import {
  CategoricalBarChartSlots,
  CategoricalBarChartToolbarConfiguration,
} from '../types/categorical-bar-chart.js';

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (
  children: ReactNode,
): CategoricalBarChartToolbarConfiguration => {
  let slots = {};

  Children.forEach(children, (childInteractions) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if (isSlot(childInteractions, DownloadData)) {
      slots = { ...slots, downloadData: { enabled: true } };
    }
  });

  return slots;
};

export const iterateSlots = (children: ReactNode) => {
  let slots: CategoricalBarChartSlots = {
    legend: {},
    tooltip: {},
    valueAxis: {},
    categoryAxis: {},
    thresholds: [],
    toolbar: {},
    colorRules: [],
    intents: [],
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Legend)) {
      const legend = child.props;

      slots = {
        ...slots,
        legend,
      };
    } else if (isSlot(child, CategoryAxis)) {
      const categoryAxis = child.props;
      slots = {
        ...slots,
        categoryAxis,
      };
    } else if (isSlot(child, ValueAxis)) {
      const valueAxis = child.props;

      slots = {
        ...slots,
        valueAxis,
      };
    } else if (isSlot(child, Tooltip)) {
      const tooltip = child.props;
      slots = {
        ...slots,
        tooltip,
      };
    } else if (isSlot(child, ThresholdIndicator)) {
      const thresholdProps =
        child.props as CategoricalBarChartThresholdIndicatorProps;
      slots.thresholds.push(thresholdProps);
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, ChartToolbar)) {
      const { children, ...toolbar } = child.props;

      const toolbarChildren = iterateChartToolbarSlots(children);

      slots = {
        ...slots,
        toolbar: {
          ...slots.toolbar,
          ...toolbar,
          ...toolbarChildren,
        },
      };
    } else if (isSlot(child, Intent)) {
      slots.intents.push(child.props);
    } else if (isSlot(child, ColorRule)) {
      const colorRule = child.props;
      if (isValidColorRule(colorRule)) {
        slots.colorRules.push(colorRule);
      }
    } else if (isSlot(child, CategoricalBarChartToolbarSlot)) {
      slots.toolbar = {
        ...slots.toolbar,
        ...sanitizeProps(child.props),
        ...extractBehaviorTrackingProps(child.props),
      };
    } else if (isSlot(child, CategoricalBarChartDownloadCSVSlot)) {
      slots.toolbar.downloadData ||= { enabled: false };
      slots.toolbar.downloadData.enabled = true;
    }
  });

  return slots;
};
